/**
 * AdminReportsScreen — exports de datos.
 *
 * Cards con descargas CSV (users, payments, plans). Snapshot completo JSON
 * por mailto (no implementado server-side todavía). Sección "Próximamente"
 * para cohort retention / churn / LTV.
 *
 * Trae el dashboard una sola vez al montar para mostrar el N total
 * ("Lista completa de N usuarios…"). Si el fetch falla solo ocultamos el N.
 */
import { useCallback, useEffect, useState } from "react";
import { useToast } from "../../lib/toast";
import { Icon } from "../../lib/Icon";
import {
  dashboard,
  downloadBlob,
  exportCsv,
  type AdminDashboardKpis,
  type CsvKind,
} from "../../api/admin";
import { AdminBadge, AdminGate, formatCOP } from "./_shared";

export function AdminReportsScreen() {
  return (
    <AdminGate>
      <Inner />
    </AdminGate>
  );
}

interface ExportCard {
  kind: CsvKind;
  icon: string;
  title: string;
  description: (k: AdminDashboardKpis | null) => string;
}

const CARDS: ExportCard[] = [
  {
    kind: "users",
    icon: "user",
    title: "Exportar usuarios",
    description: (k) =>
      k
        ? `Lista completa de ${k.users_total} coaches con tier, planes activos y fecha de registro.`
        : "Lista completa de coaches con tier, planes activos y fecha de registro.",
  },
  {
    kind: "payments",
    icon: "money",
    title: "Exportar pagos",
    description: (k) =>
      k
        ? `${k.payments_total_count} pagos registrados (manuales + auto). Incluye monto, método y referencia.`
        : "Pagos registrados (manuales + auto). Incluye monto, método y referencia.",
  },
  {
    kind: "plans",
    icon: "book",
    title: "Exportar planes",
    description: (k) =>
      k
        ? `${k.plans_total} planes generados con coach, cliente y plantilla.`
        : "Planes generados con coach, cliente y plantilla.",
  },
];

function Inner() {
  const toast = useToast();
  const [kpis, setKpis] = useState<AdminDashboardKpis | null>(null);
  const [busy, setBusy] = useState<CsvKind | null>(null);

  useEffect(() => {
    let cancelled = false;
    dashboard()
      .then((k) => { if (!cancelled) setKpis(k); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  const onDownload = useCallback(async (kind: CsvKind) => {
    if (busy) return;
    setBusy(kind);
    try {
      const blob = await exportCsv(kind);
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `aetherlite-${kind}-${stamp}.csv`);
      toast(`CSV de ${kind} descargado`);
    } catch (e) {
      toast("No pude exportar: " + (e instanceof Error ? e.message : ""));
    } finally {
      setBusy(null);
    }
  }, [busy, toast]);

  return (
    <div className="page al-admin-shell">
      <div className="al-admin-head">
        <div>
          <h1 className="al-page-title">
            Reportes <AdminBadge />
          </h1>
          <p className="muted">Exporta data cruda para análisis externo.</p>
        </div>
      </div>

      {/* Cards de exports */}
      <div className="al-admin-reports-grid">
        {CARDS.map((c) => (
          <div className="card al-admin-report-card" key={c.kind}>
            <div className="al-admin-report-icon">
              <Icon name={c.icon} size={28} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="al-admin-report-title">{c.title}</div>
              <p className="tiny muted" style={{ margin: "4px 0 0" }}>{c.description(kpis)}</p>
            </div>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => onDownload(c.kind)}
              disabled={busy !== null}
            >
              <Icon name="upload" size={14} />
              {busy === c.kind ? "Descargando…" : "Descargar"}
            </button>
          </div>
        ))}

        {/* Snapshot JSON (no implementado server-side) */}
        <div className="card al-admin-report-card">
          <div className="al-admin-report-icon">
            <Icon name="sparkle" size={28} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="al-admin-report-title">Snapshot completo (JSON)</div>
            <p className="tiny muted" style={{ margin: "4px 0 0" }}>
              Backup integral de la base (users + planes + share-links + pagos).
              Procesado manualmente por Nilabs.
            </p>
          </div>
          <a
            className="btn btn-secondary btn-sm"
            href="mailto:contacto@nilabs.co?subject=Snapshot%20AetherLite"
          >
            <Icon name="send" size={14} /> Solicitar
          </a>
        </div>
      </div>

      {/* Métricas del sistema (placeholder honesto) */}
      <div className="card al-admin-card al-admin-soft">
        <div className="card-header">
          <div className="card-title">
            <Icon name="sparkle" size={14} /> Métricas del sistema
          </div>
          <span className="chip">Próximamente</span>
        </div>
        <p className="tiny muted" style={{ marginTop: 6 }}>
          Aquí verás cohort retention, churn mensual, LTV por tier y tiempo de
          vida promedio del coach. Mientras tanto, exportá a CSV y abrilo en
          Sheets para análisis ad hoc.
        </p>
        {kpis && (
          <div className="al-admin-mini-grid">
            <Mini label="Usuarios" value={String(kpis.users_total)} />
            <Mini label="Activos 30d" value={String(kpis.users_active_30d)} />
            <Mini label="Planes" value={String(kpis.plans_total)} />
            <Mini label="Vistas" value={String(kpis.shares_views_total)} />
            <Mini label="MRR estimado" value={formatCOP(kpis.mrr_active_cop)} />
            <Mini label="Pagos último mes" value={formatCOP(kpis.payments_total_cop)} />
          </div>
        )}
      </div>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div className="al-admin-mini">
      <div className="al-admin-mini-label">{label}</div>
      <div className="al-admin-mini-value">{value}</div>
    </div>
  );
}
